import dayjs from 'dayjs';
import { InputChoice } from 'components/Inputs/Dropdown/dropdownConsts';
import { CoreLogLevel } from 'providers/contexts/extensions/CoreLogs';

/**
 * Log search shared constants
 */

/** All selectable entry levels, in the order the API documents them. */
export const LOG_LEVELS: CoreLogLevel[] = [
  'debug',
  'info',
  'warning',
  'notice',
  'error',
  'fatal',
];

/** How many entries to request per page. The API caps/paginates via `next` token. */
export const LOGS_PER_PAGE = 100;

/**
 * Maximum span (in days) allowed for the Date range filter. The log backend
 * errors/times out on wide windows, so a selected range is clamped.
 */
export const MAX_DATE_RANGE_DAYS = 14;

/**
 * Clamp a `[start, end]` day range (YYYY-MM-DD) to at most `maxDays`. When both
 * ends are set and the span is too wide, the start is moved forward to keep the
 * most recent `maxDays` window (favoring recent entries). Partial or invalid
 * ranges are returned unchanged.
 */
export const clampDateRangeToMaxDays = (
  range: [string, string],
  maxDays: number = MAX_DATE_RANGE_DAYS,
): [string, string] => {
  const [start, end] = range;
  if (!start || !end) {
    return range;
  }
  const startDay = dayjs(start);
  const endDay = dayjs(end);
  if (!startDay.isValid() || !endDay.isValid() || endDay.isBefore(startDay)) {
    return range;
  }
  if (endDay.diff(startDay, 'day') <= maxDays) {
    return range;
  }
  return [endDay.subtract(maxDays, 'day').format('YYYY-MM-DD'), end];
};

/**
 * Build the Level multi-select choices. Pass `t` so labels stay translated and
 * the keys remain literal strings at the call site.
 */
export const getLogLevelChoices = (t: (key: string) => string): InputChoice[] => [
  { value: 'debug', label: t('blox.logs.levels.debug') },
  { value: 'info', label: t('blox.logs.levels.info') },
  { value: 'warning', label: t('blox.logs.levels.warning') },
  { value: 'notice', label: t('blox.logs.levels.notice') },
  { value: 'error', label: t('blox.logs.levels.error') },
  { value: 'fatal', label: t('blox.logs.levels.fatal') },
];
